import { Warning } from '@mui/icons-material';
import { find, forEach } from 'lodash';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { groupsApi } from 'api';
import { Math } from 'classes';
import {
  DataGrid,
  DataGridColumnDefinition,
  DataWrapper,
  PaginationWrapper,
  QueryFilters,
  StylizedNumber,
} from 'components';
import { FilterDefinitionType, OrderType, QueryKey } from 'enums';
import { usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, Group, Shop, Trader, User } from 'types';
import { userUtils } from 'utils';

import { AgentsInfo } from '../AgentsInfo';

type Props = {
  orderType: OrderType;
  isLoading: boolean;
  users?: User[];
  traders?: Trader[];
  shops?: Shop[];
};

type GroupsFilters = {
  name: string;
  groupId: string;
  shopId: string;
  traderId: string;
  payin: boolean;
  payout: boolean;
};

type CashbackWithRelations = {
  id: string;
  group: Group;
  shop?: Shop;
  trader?: Trader;
  user?: User;
};

export const GroupsCashback: React.FC<Props> = ({
  users,
  traders,
  shops,
  orderType,
  isLoading,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.cashback.groups',
  });
  const { t: tGroups } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.groups',
  });

  const isPayin = useMemo(() => orderType === OrderType.Payin, [orderType]);

  const filtersDefinitions: FilterDefinition<GroupsFilters>[] = useMemo(
    () => [
      {
        label: tGroups('filters.id'),
        name: 'groupId',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: tGroups('filters.name'),
        name: 'name',
        type: FilterDefinitionType.Text,
      },
      {
        label: tGroups('filters.trader'),
        name: 'traderId',
        type: FilterDefinitionType.Trader,
        traders,
      },
      {
        label: tGroups('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        users: shops,
      },
    ],
    [shops, tGroups, traders],
  );

  const queryResult = usePartialQuery(
    [QueryKey.Groups, orderType],
    groupsApi.getForOrderTypePaginated(orderType),
  );

  const getTraderCompensation = useCallback(
    (trader?: Trader): number =>
      (isPayin ? trader?.compensation : trader?.payoutCompensation) || 0,
    [isPayin],
  );

  const getShopCommission = useCallback(
    (shop?: Shop) => (isPayin ? shop?.commission : shop?.payoutCommission) || 0,
    [isPayin],
  );

  const getPlatformCommission = useCallback(
    (item: CashbackWithRelations): number => {
      const math = new Math(getShopCommission(item.shop));
      math.minus(
        userUtils.getAgentsCommission(item.shop?.agents || [], isPayin),
        getTraderCompensation(item.trader),
        userUtils.getAgentsCommission(item.user?.agents || [], isPayin),
      );
      return math.value;
    },
    [getShopCommission, getTraderCompensation, isPayin],
  );

  const columns: DataGridColumnDefinition<CashbackWithRelations>[] = useMemo(
    () => [
      {
        header: '',
        valueGetter: (item) => {
          if (getPlatformCommission(item) < 0) {
            return <Warning color="warning" />;
          }
        },
      },
      {
        header: t('columns.group'),
        valueGetter: (item) => <div>{item.group.name}</div>,
      },
      {
        header: t('columns.shop'),
        valueGetter: (item) =>
          item.shop ? (
            <div className="tw-flex">
              <div className="tw-mr-2">{item.shop.name}</div>
              <StylizedNumber value={getShopCommission(item.shop)} unit="%" />
            </div>
          ) : null,
      },
      {
        header: t('columns.shop_agents'),
        valueGetter: (item) => (
          <AgentsInfo
            agents={item.shop?.agents}
            isPayin={isPayin}
            users={users}
          />
        ),
      },
      {
        header: t('columns.trader'),
        valueGetter: (item) =>
          item.user ? (
            <div className="tw-flex">
              <div className="tw-pr-2">{item.user.name}</div>
              <StylizedNumber
                value={getTraderCompensation(item.trader)}
                unit="%"
              />
            </div>
          ) : null,
      },
      {
        header: t('columns.trader_agents'),
        valueGetter: (item) => (
          <AgentsInfo
            agents={item.user?.agents}
            isPayin={isPayin}
            users={users}
          />
        ),
      },
      {
        header: t('columns.platform_commission'),
        valueGetter: (item) => (
          <StylizedNumber value={getPlatformCommission(item)} unit="%" />
        ),
      },
    ],
    [
      getPlatformCommission,
      getShopCommission,
      getTraderCompensation,
      isPayin,
      users,
      t,
    ],
  );

  const data: CashbackWithRelations[] = useMemo(() => {
    const cashbackData: CashbackWithRelations[] = [];
    forEach(queryResult.data?.items, (group) => {
      forEach(group.shops, (_shop) => {
        const shop = find(shops, { id: _shop.id });
        forEach(group.traders, ({ id: traderId }) => {
          const trader = find(traders, (trader) => trader.id === traderId);
          const user = find(users, (user) => trader?.userId === user.id);
          cashbackData.push({
            id: `${group.id}_${_shop.id}_${traderId}`,
            group,
            shop,
            user,
            trader,
          });
        });
      });
    });
    return cashbackData;
  }, [queryResult, shops, users, traders]);

  return (
    <Fragment>
      <QueryFilters filtersDefinitions={filtersDefinitions} />
      <DataWrapper isLoading={isLoading}>
        <PaginationWrapper
          queryResult={queryResult}
          ignoreState={{ refetchLoading: true }}
        >
          <DataGrid columns={columns} data={data} />
        </PaginationWrapper>
      </DataWrapper>
    </Fragment>
  );
};
